import { ConfigProvider, Flex, Layout, Tabs, Typography, Button } from 'antd';
import React, { useState, useEffect, useContext } from 'react';
import './SafetyAndAnomalyMonitoring.css';
import { useLocation } from 'react-router-dom';
import AnomalyLogs from './components/AnomalyLogs/AnomalyLogs';
import Icon from '@ant-design/icons';
import { ReactComponent as Add } from '../../assets/icons/add.svg';
import { ReactComponent as AddBlack } from '../../assets/icons/add-black.svg';
import { ErrorMessageContext } from '../../context/ErrorMessageContext';
import { useNotification } from '../../context/NotificationContext';
import { ReactComponent as cancel } from '../../assets/icons/cancel.svg';
import MonitorSafety from './components/MonitorSafety/MonitorSafety';
import SOSLogs from './components/SOSLogs/SOSLogs';
import SOTicketModal from './components/SOSLogs/SOTicketModal';
import { useTheme } from '../../context/ThemeContext';
import { ReactComponent as cancelBlack } from '../../assets/icons/cancel-black.svg';
import { DARK, PAGE_KEYS } from '../../utilities/constants';
import { UserRolesAndPermissionsContext } from '../../context/UserRolesAndPermissionsContext';
import { formatDateTime } from '../../utilities/commonFunctions';

const { Text } = Typography;
const { Content } = Layout;

const SafetyAndAnomalyMonitoring = () => {
  const { addErrorMessage, removeErrorMessage } =
    useContext(ErrorMessageContext);
  const [activeKey, setActiveKey] = useState('1');
  const [selectedTabKey, setSelectedTabKey] = useState(0);
  const [isSoTicketModelOpen, setIsSoTicketModalOpen] = useState(false);
  const [sosListRefreshKey, setSosListRefreshKey] = useState(0);
  const location = useLocation();
  const { card } = location.state || '';
  const { theme } = useTheme();
  const { isViewOnly } = useContext(UserRolesAndPermissionsContext);
  const [lastUpdatedTime, setLastUpdatedTime] = useState(Date.now());
  const shouldShowLastUpdated = activeKey === '1' || activeKey === '2';
  const shouldShowCreateSoTicket =
    (activeKey === '1' || activeKey === '3') &&
    !isViewOnly(PAGE_KEYS.SAFETY_AND_ANOMALY_MONITORING);

  const {
    isFirstLoginSession,
    cancelMonitorSafetyNotification,
    cancelMapViewNotification,
  } = useNotification();

  const addMonitorSafetyNotification = () => {
    if (isFirstLoginSession?.safetyAndAnomalyMonitorSafety) {
      addErrorMessage(
        <Flex gap={16} align="center">
          <Text>The data shown here is of the last 24 hours.</Text>
          <Icon
            component={theme === DARK ? cancel : cancelBlack}
            onClick={() => {
              removeErrorMessage('monitor-safety-notification');
              cancelMonitorSafetyNotification();
            }}
          />
        </Flex>,
        'notify',
        'monitor-safety-notification'
      );
    }
  };

  const addMapViewNotification = () => {
    if (isFirstLoginSession?.safetyAndAnomalyMapView) {
      addErrorMessage(
        <Flex gap={16} align="center">
          <Text>The data shown here is of the last 24 hours.</Text>
          <Icon
            component={theme === DARK ? cancel : cancelBlack}
            onClick={() => {
              removeErrorMessage('map-view-notification');
              cancelMapViewNotification();
            }}
          />
        </Flex>,
        'notify',
        'map-view-notification'
      );
    }
  };

  useEffect(() => {
    if (activeKey === '1') {
      removeErrorMessage('map-view-notification');
      addMonitorSafetyNotification();
    } else if (activeKey === '2') {
      removeErrorMessage('monitor-safety-notification');
      addMapViewNotification();
    } else {
      removeErrorMessage('monitor-safety-notification');
      removeErrorMessage('map-view-notification');
    }
  }, [activeKey]);

  useEffect(() => {
    if (activeKey === '1') {
      addMonitorSafetyNotification();
    } else if (activeKey === '2') {
      addMapViewNotification();
    }
    return () => {
      removeErrorMessage('monitor-safety-notification');
      removeErrorMessage('map-view-notification');
    };
  }, [theme]);

  useEffect(() => {
    if (card === undefined || card === 'SOS Alerts') {
      setSelectedTabKey(0);
    } else if (card === 'Driver Drowsiness') {
      setActiveKey('4');
    } else {
      setSelectedTabKey(1);
    }
  }, []);

  const handleSoTicketSuccess = () => {
    setSosListRefreshKey(prevKey => prevKey + 1);
  };

  return (
    <ConfigProvider
      theme={{
        components: {
          Typography: {
            titleMarginBottom: 0,
            titleMarginTop: 0,
            fontSize: '1rem',
          },
          Layout: {
            siderBg: 'var(--tab-bg-color, #232628)',
            headerBg: 'transparent',
            headerPadding: '0',
          },
          Form: {
            labelColonMarginInlineEnd: 0,
            labelColonMarginInlineStart: 0,
            itemMarginBottom: 0,
          },
          Input: {
            colorBgContainer: 'var(--broadcast_form-input-bg-color, #2E3033)',
            paddingBlock: '0.75rem',
          },
          InputNumber: {
            colorBgContainer: 'var(--broadcast_form-input-bg-color, #2E3033)',
            fontSize: '1rem',
            colorBorder: 'transparent',
            colorTextPlaceholder: 'var(--placeholder-text-color, #ffffff80)',
            paddingBlock: '0.75rem',
          },
          Select: {
            controlHeight: '3rem',
            fontSize: '1rem',
            lineHeight: '1.5rem',
            selectorBg: 'var(--broadcast_form-input-bg-color, #2E3033)',
          },
        },
        token: {
          fontSize: '0.875rem',
          colorSplit: '#656A6F',
        },
      }}
    >
      <Layout className="safety-and-anomaly-monitoring">
        <Content className="full-height">
          <Tabs
            type="card"
            size="large"
            className="full-height page-tabs"
            tabBarGutter="2rem"
            tabBarExtraContent={
              shouldShowLastUpdated || shouldShowCreateSoTicket ? (
                <Flex gap={24} align="center">
                  {shouldShowLastUpdated && (
                    <Text className="last-updated-time">
                      {formatDateTime(lastUpdatedTime)}
                    </Text>
                  )}
                  {shouldShowCreateSoTicket && (
                    <Button
                      type="primary"
                      onClick={() => {
                        setIsSoTicketModalOpen(true);
                      }}
                    >
                      <Flex gap={8}>
                        <Icon component={theme === DARK ? AddBlack : Add} />
                        Create SO Ticket
                      </Flex>
                    </Button>
                  )}
                </Flex>
              ) : null
            }
            tabBarStyle={{
              padding: '0 0 0 1rem',
            }}
            destroyOnHidden={false}
            defaultActiveKey="1"
            activeKey={activeKey}
            items={[
              {
                key: '1',
                label: 'Monitor Safety',
                children: (
                  <MonitorSafety
                    selectedTabKey={selectedTabKey}
                    setSelectedTabKey={setSelectedTabKey}
                    activeKey={activeKey}
                    setLastUpdatedTime={setLastUpdatedTime}
                    sosListRefreshKey={sosListRefreshKey}
                  />
                ),
              },
              {
                key: '2',
                label: 'Map View',
                children: (
                  <MonitorSafety
                    selectedTabKey={selectedTabKey}
                    setSelectedTabKey={setSelectedTabKey}
                    activeKey={activeKey}
                    mapView={true}
                    setLastUpdatedTime={setLastUpdatedTime}
                    sosListRefreshKey={sosListRefreshKey}
                  />
                ),
              },
              {
                key: '3',
                label: 'SOS Tickets',
                children: <SOSLogs />,
              },
              {
                key: '4',
                label: 'Anomaly Logs',
                children: <AnomalyLogs />,
              },
            ]}
            onChange={activeKey => setActiveKey(activeKey)}
          />
        </Content>
      </Layout>
      <SOTicketModal
        isModalOpen={isSoTicketModelOpen}
        setIsModalOpen={setIsSoTicketModalOpen}
        onCreateSuccess={handleSoTicketSuccess}
      />
    </ConfigProvider>
  );
};

export default React.memo(SafetyAndAnomalyMonitoring);
